import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import type { User, HouseholdMember, HouseholdInvite, Household } from "../types";

interface HouseholdResponse {
  household: Household;
  members: HouseholdMember[];
  invites: HouseholdInvite[];
}

export default function FamilyPage({ user }: { user: User }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["household"],
    queryFn: () => api.get<HouseholdResponse>("/api/household"),
  });
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");

  const renameMut = useMutation({
    mutationFn: (n: string) => api.patch("/api/household", { name: n }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  });

  const inviteMut = useMutation({
    mutationFn: (email: string | null) => api.post<{ token: string }>("/api/household/invite", { email }),
    onSuccess: () => {
      setInviteEmail("");
      qc.invalidateQueries({ queryKey: ["household"] });
    },
  });

  const deleteInviteMut = useMutation({
    mutationFn: (token: string) => api.delete(`/api/household/invite?token=${encodeURIComponent(token)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["household"] }),
  });

  if (isLoading || !data) {
    return <p className="text-slate-500">Načítám…</p>;
  }

  const inviteLink = (token: string) => `${window.location.origin}/rodina/pozvanka/${token}`;

  return (
    <div>
      <PageHeader icon="👨‍👩‍👧" title="Rodina" description="Spravujte svou domácnost a její členy." />

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>🏡</span>
          <span>Domácnost</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder={data.household.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!name.trim() || renameMut.isPending}
            onClick={() => renameMut.mutate(name.trim())}
          >
            <span aria-hidden>✏️</span>
            <span>Přejmenovat</span>
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Aktuálně: {data.household.name}</p>
      </div>

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>👥</span>
          <span>Členové ({data.members.length})</span>
        </h2>
        <ul className="divide-y">
          {data.members.map((m) => (
            <li key={m.id} className="py-3 flex items-center gap-3">
              {m.pictureUrl && (
                <img src={m.pictureUrl} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {m.name} {m.id === user.id && <span className="text-xs text-brand-700">(vy)</span>}
                </div>
                <div className="text-xs text-slate-500">{m.email}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="card card-body">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>✉️</span>
          <span>Pozvat dalšího člena</span>
        </h2>
        <p className="text-sm text-slate-500 mb-3">
          Vygenerujte odkaz a pošlete ho komukoli ve své rodině. Po přihlášení přes Google se připojí do vaší
          domácnosti. Odkaz je platný 7 dní.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <input
            className="input"
            type="email"
            placeholder="email@priklad.cz (volitelné)"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <button
            className="btn-primary flex items-center gap-1"
            onClick={() => inviteMut.mutate(inviteEmail.trim() || null)}
            disabled={inviteMut.isPending}
          >
            <span aria-hidden>✉️</span>
            <span>Vytvořit pozvánku</span>
          </button>
        </div>

        {data.invites.length === 0 ? (
          <p className="text-sm text-slate-400">Žádné aktivní pozvánky</p>
        ) : (
          <ul className="divide-y">
            {data.invites.map((i) => (
              <li key={i.token} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{i.email || "(bez emailu)"}</div>
                  <div className="text-xs text-slate-500 truncate">{inviteLink(i.token)}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn-secondary text-xs flex items-center gap-1"
                    onClick={() => {
                      navigator.clipboard.writeText(inviteLink(i.token));
                    }}
                    title="Zkopírovat odkaz do schránky"
                  >
                    <span aria-hidden>📋</span>
                    <span>Zkopírovat odkaz</span>
                  </button>
                  <button
                    className="btn-danger text-xs flex items-center gap-1"
                    onClick={() => deleteInviteMut.mutate(i.token)}
                    title="Zrušit pozvánku"
                  >
                    <span aria-hidden>🗑️</span>
                    <span>Zrušit</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
